import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import PageHeader from '../components/ui/PageHeader';
import { FadeIn } from '../components/ui/AnimatedSection';
import Calculator from '../features/pricing/Calculator';
import { useTranslation } from 'react-i18next';

export default function Faq() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqData = t('faqPage.data', { returnObjects: true }) as Array<{category: string; items: Array<{q: string; a: string}>}>;

  return (
    <div className="min-h-screen flex flex-col font-sans text-black-deep bg-cream-100">
      <Navbar />
      <PageHeader 
        title={t('faqPage.headerTitle')} 
        subtitle={t('faqPage.headerSubtitle')} 
        imagePath="/assets/luxury_interior.webp" 
      />

      <main className="flex-grow py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col lg:flex-row gap-12">
            
            {/* Sidebar Categories */}
            <div className="w-full lg:w-1/3">
              <div className="sticky top-32 space-y-2">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-6">{t('faqPage.themes')}</h3>
                {faqData.map((cat, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setActiveCategory(idx); setOpenIndex(0); }}
                    className={`w-full text-left px-6 py-4 rounded-sm transition-all duration-300 font-serif text-lg ${
                      activeCategory === idx 
                        ? 'bg-black-deep text-gold-400 shadow-md' 
                        : 'bg-white text-gray-500 hover:bg-cream-50 hover:text-black-deep border border-gray-100'
                    }`}
                  >
                    {cat.category}
                  </button>
                ))}
              </div>
            </div>

            {/* FAQ Accordion */}
            <div className="w-full lg:w-2/3">
              <FadeIn key={activeCategory}>
                <h2 className="text-3xl font-serif text-black-deep mb-8 pb-4 border-b border-gold-200">
                  {faqData[activeCategory].category}
                </h2>
                <div className="space-y-4">
                  {faqData[activeCategory].items.map((faq, index) => (
                    <div key={index} className="bg-white border border-gray-100 rounded-sm overflow-hidden shadow-sm hover:border-gold-300 transition-colors">
                      <button
                        onClick={() => setOpenIndex(openIndex === index ? null : index)}
                        className="w-full px-6 py-5 flex justify-between items-center bg-white focus:outline-none"
                      >
                        <span className={`font-serif text-lg text-left pr-8 ${openIndex === index ? 'text-gold-600' : 'text-black-deep'}`}>
                          {faq.q}
                        </span>
                        {openIndex === index 
                          ? <ChevronUp className="text-gold-500 flex-shrink-0" /> 
                          : <ChevronDown className="text-gray-300 flex-shrink-0" />}
                      </button>
                      
                      <div 
                        className={`transition-all duration-300 ease-in-out ${
                          openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        } overflow-hidden`}
                      >
                        <div className="px-6 pb-6 text-gray-500 font-light leading-relaxed">
                          <div className="w-8 h-px bg-gold-200 mb-4"></div>
                          {faq.a}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </FadeIn>
            </div>
          </div>

        </div>
      </main>

      <Calculator />
      <Footer />
    </div>
  );
}
